import React from 'react';
import { useCurrentAgreement, useLocalStorage } from '@economic/react-app';
import { addDays } from 'date-fns';
import { AxiosError } from 'axios';
import restApiClient from '../util/api/restApiClient';

interface AppSettingsContent<T> {
    content: T;
    // internal keys
    // settingKey: string;
    // self: string;
}

interface AppSettingsOptions {
    skip: boolean;
    isAgreementSetting: boolean;
}

export interface AppSettingsResult<T> {
    data: AppSettingsContent<T> | undefined;
    loading: boolean;
    error: AxiosError | Error | undefined;
    refetch: () => Promise<void>;
    set: (data: T) => Promise<void>;
}

type AppSettingsState<T> = Pick<AppSettingsResult<T>, 'data' | 'loading' | 'error'>;

// FIXME: inconsistency between get/set

const getInitialData = <T>(initialData: T) => (initialData ? { content: initialData } : undefined);

/**
 * Retrieves app settings data or sets app settings data.
 * @param {string} settingKey - The key of the app setting.
 * @param {any} initialData - The initial data to be used if the app setting data is not available.
 * @param {Object} options - Additional options for the method.
 * @param {boolean} options.skip - Flag to skip fetching the app setting data.
 * @param {boolean} options.isAgreementSetting - Flag to indicate if the app setting is an agreement setting.
 * @return {Object} - The app settings result object.
 *     - data: The app setting data.
 *     - loading: Flag indicating if the app setting data is being loaded.
 *     - error: The error that occurred while fetching or setting the app setting data.
 *     - refetch: Function to manually refetch the app setting data.
 *     - set: Function to set the app setting data.
 * FIXME: replace any with unknown
 */
export default function useAppSettings<T = any>(
    settingKey: string,
    initialData: T | undefined = undefined,
    options: Partial<AppSettingsOptions> = {}
): AppSettingsResult<T> {
    const [state, setState] = React.useState<AppSettingsState<T>>({
        loading: !options.skip,
        data: getInitialData(initialData),
        error: undefined,
    });
    const path = options.isAgreementSetting ? `/app-settings/agreement/${settingKey}` : `/app-settings/user/${settingKey}`;
    // From Axios docs https://axios-http.com/docs/req_config
    // `timeout` specifies the number of milliseconds before the request times out.
    // If the request takes longer than `timeout`, the request will be aborted.
    // The default is `0` (no timeout)

    // 10s to give the rest api enough time to respond
    const timeout = 10000;

    const handleError = (error: Error | AxiosError) => {
        // TODO error instanceof AxiosError, currently not working (maybe because two instances of axios local vs http-client)
        if ((error as AxiosError)?.response && (error as AxiosError).response!.status >= 500) {
            setState({ loading: false, data: getInitialData(initialData), error });
        } else {
            setState({ loading: false, data: getInitialData(initialData), error: undefined });
        }
    };

    const fetch = async (url: string) => {
        try {
            const response = await restApiClient.get(url, { timeout });
            setState({ loading: false, data: response.data, error: undefined });
        } catch (error) {
            handleError(error);
        }
    };

    const set = async (data: any) => {
        try {
            const response = await restApiClient.put(path, { settingKey, content: data }, { timeout });
            setState({ loading: false, data: response.data, error: undefined });
        } catch (error) {
            handleError(error);
        }
    };

    React.useEffect(() => {
        if (!options.skip) {
            fetch(path);
        }
    }, [path, options.skip]);

    return {
        data: state.data,
        loading: state.loading,
        error: state.error,
        refetch: () => fetch(path),
        set,
    };
}

export function useCachedAppSettings<T>(settingKey: string): AppSettingsResult<T> {
    const agreement = useCurrentAgreement();
    const localStorageKey = `app-settings/user/${agreement.number}/${settingKey}`;
    const [settings, setSettings] = useLocalStorage<{ data: any; timestamp: number } | undefined>(localStorageKey);

    const expired = addDays(settings?.timestamp ?? 0, 1).getTime() < Date.now();
    const skipFetch = !!settings && !expired;

    const appSettings = useAppSettings<T>(settingKey, settings?.data, { skip: skipFetch });

    React.useEffect(() => {
        if (appSettings.data !== undefined) {
            setSettings({ data: appSettings.data, timestamp: Date.now() });
        }
    }, [appSettings.data]);

    return appSettings;
}
